import { cn } from '@/shared/lib/utils';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * RDS Context Menu — DropdownMenu에 매핑.
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=234-5540
 */
export const dropdownMenuContentClassName = cn(
  'z-50 min-w-[240px] overflow-hidden rounded-20 border-0 bg-white p-0 py-2',
  'text-ruda-text-primary shadow-[0_6px_14px_-6px_rgba(19,25,39,0.12),0_10px_32px_-4px_rgba(19,25,39,0.1)]',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
);

export const dropdownMenuItemVariants = cva(
  [
    'relative flex w-full cursor-default select-none items-center gap-3',
    'h-12 px-4 text-base font-normal leading-6 tracking-tight outline-none',
    'text-ruda-text-primary transition-colors',
    '[&>svg]:size-6 [&>svg]:shrink-0',
    'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
    // Hover → Primary-50
    'data-[highlighted]:bg-ruda-primary-50 data-[highlighted]:text-ruda-text-primary',
    // Press → Primary-100
    'active:bg-ruda-primary-100',
  ],
  {
    variants: {
      variant: {
        default: '',
        /** Negative — 삭제 등 위험 액션 */
        destructive: [
          'font-semibold text-[#d93e39]',
          'data-[highlighted]:bg-ruda-primary-50 data-[highlighted]:text-[#d93e39]',
          '[&>svg]:text-[#d93e39]',
        ],
      },
      selected: {
        true: [
          'font-semibold text-ruda-primary-600',
          'data-[highlighted]:text-ruda-primary-600',
          '[&>svg]:text-ruda-primary-600',
        ],
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      selected: false,
    },
  },
);

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(dropdownMenuContentClassName, className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

type DropdownMenuItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> &
  VariantProps<typeof dropdownMenuItemVariants> & {
    inset?: boolean;
  };

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, inset, variant, selected, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      dropdownMenuItemVariants({ variant, selected }),
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('my-1 h-px bg-ruda-border-light', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuTrigger,
};
